import { Link } from '@tanstack/react-router';
import { Archive as ArchiveIcon } from 'lucide-react';
import type { ComponentType, ElementType, ReactNode } from 'react';
import {
  AvatarStack,
  Body,
  Card,
  CardLinkAction,
  Heading,
  I,
  Pill,
  Row,
  Stack,
  Thumbnail,
  interactiveBg,
  useInteractive,
  type LinkAsProps,
  type Person,
  type Tone,
} from '@leanin/ui';

type Variant = 'listRow' | 'card';

type CircleProps = {
  readonly kind: 'circle';
};

type NetworkProps = {
  readonly kind: 'network';
  readonly circleCount?: number;
};

type SpaceProps = {
  readonly kind: 'space';
  readonly postCount?: number;
};

type CommonProps = {
  readonly href: string;
  readonly ariaLabel: string;
  readonly linkAs?: ElementType | ComponentType<LinkAsProps>;
  readonly name: string;
  readonly src?: string;
  readonly tone?: Tone;
  readonly memberCount: number;
  readonly leaders?: ReadonlyArray<Person>;
  /** When true, prepends a "Closed" pill to the meta line. Suppressed
   *  when `archived` is true — Archived takes visual precedence. */
  readonly closed?: boolean;
  /** When true, prepends an "Archived" pill and dims the row body. Mirrors
   *  the archived treatment on `GroupCard`. */
  readonly archived?: boolean;
  /** Right-edge action (Join button, role pill, etc.). Auto-wrapped in
   *  `CardLinkAction` so clicks don't bubble to the row overlay link. */
  readonly trailing?: ReactNode;
  /** `'listRow'` (default) renders a bare interactive row for use inside a
   *  `Card pad="flush" clip`. `'card'` wraps the same content in a
   *  `Card pad="default"` for standalone, stacked layouts. */
  readonly variant?: Variant;
};

export type GroupRowProps = CommonProps & (CircleProps | NetworkProps | SpaceProps);

const KIND_DEFAULT_TONE: Record<GroupRowProps['kind'], Tone> = {
  circle: 'burgundy',
  network: 'navy',
  space: 'emerald',
};

function metaSegments(props: GroupRowProps): string[] {
  const memberNoun = props.memberCount === 1 ? 'member' : 'members';
  const out: string[] = [`${props.memberCount.toLocaleString()} ${memberNoun}`];
  if (props.kind === 'network' && props.circleCount !== undefined) {
    out.push(`${props.circleCount} ${props.circleCount === 1 ? 'Circle' : 'Circles'}`);
  } else if (props.kind === 'space' && props.postCount !== undefined) {
    const postNoun = props.postCount === 1 ? 'post' : 'posts';
    out.push(`${props.postCount.toLocaleString()} ${postNoun}`);
  }
  return out;
}

export function GroupRow(props: GroupRowProps) {
  const {
    href, ariaLabel, linkAs, name, src, tone, leaders = [], closed,
    archived = false, trailing, variant = 'listRow',
  } = props;
  const { state, bind } = useInteractive();
  const LinkAs = (linkAs ?? Link) as ElementType;

  const body: ReactNode = (
    <>
      <LinkAs
        href={href}
        aria-label={ariaLabel}
        style={{ position: 'absolute', inset: 0, zIndex: 'var(--z-card-overlay)' }}
      />
      <Row
        gap="md"
        align="center"
        style={{ minWidth: 0, ...(archived ? { opacity: 0.7 } : {}) }}
      >
        <Thumbnail
          {...(src ? { src } : {})}
          tone={tone ?? KIND_DEFAULT_TONE[props.kind]}
          size={44}
        />
        <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
          <Row gap="sm" align="center" wrap style={{ minWidth: 0 }}>
            <Heading level={5}>{name}</Heading>
            {archived ? (
              <Pill
                tone="neutral"
                icon={<ArchiveIcon size="100%" strokeWidth={1.75} />}
              >
                Archived
              </Pill>
            ) : null}
          </Row>
          <Row gap="sm" align="center" wrap style={{ minWidth: 0 }}>
            {leaders.length > 0 ? (
              <AvatarStack people={leaders.slice()} size={20} />
            ) : null}
            {!archived && closed ? (
              <Pill tone="neutral" icon={I.lock}>Closed</Pill>
            ) : null}
            <Body size="sm" color="mute">
              {metaSegments(props).join(' · ')}
            </Body>
          </Row>
        </Stack>
        {trailing != null ? <CardLinkAction>{trailing}</CardLinkAction> : null}
      </Row>
    </>
  );

  if (variant === 'card') {
    return (
      <Card pad="default" interactive style={{ position: 'relative' }}>
        {body}
      </Card>
    );
  }

  return (
    <div
      {...bind}
      style={{
        position: 'relative',
        // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- raw <div> wraps an absolute-positioned Link overlay; no Box prop
        padding: 'var(--space-lg)',
        background: interactiveBg(state),
      }}
    >
      {body}
    </div>
  );
}
